import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, PaperPlaneTilt, Cube, CheckCircle, Sparkle, Storefront, Handshake, Star, ChartLineUp, Presentation, Tag } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { Button } from "./ui/button";

const PER_SHOOT_VALUE = 124; // ~25% of All-Inclusive: the value of the 360 tour + drone per shoot

const INCLUDED = [
  { Icon: Cube, t: "360° virtual walkthrough", d: "An immersive Matterport-style tour on every All-Inclusive shoot." },
  { Icon: PaperPlaneTilt, t: "Drone photo + video", d: "Aerial stills and cinematic flyovers included, no add-on fee." },
  { Icon: Sparkle, t: "Priority scheduling", d: "Partner shoots jump the queue when slots are tight." },
  { Icon: Star, t: "Locked-in pricing", d: "Your partner rate is locked in — no surprise increases." },
  { Icon: Presentation, t: "Unlimited listing presentations", d: "Generate free, on-brand listing presentation decks anytime — unlimited, as a Penthouse partner." },
  { Icon: Tag, t: "25% off every shoot", d: "Partner pricing on any shoot. Choose All-Inclusive without 360/drone and it's just ~$371 — pro photo, branded video & virtual staging." },
];

const OPTIONS = [
  { t: "Photo Only — $279", d: "30–50 magazine-grade photos, interior + exterior." },
  { t: "All-Inclusive — $495", d: "Photos + branded & MLS video + virtual staging/removal." },
  { t: "Skip interior → save 25%", d: "Land, construction or aerials only? We drop $135 automatically." },
  { t: "Large property", d: "Up to 5k sqft / 30 ac included; only +$99 or +$198 beyond." },
];

const TOOLS = [
  { Icon: Storefront, t: "Branded booking page", d: "Your own /p link clients book from in seconds." },
  { Icon: Handshake, t: "Referral links that pay", d: "Earn 10% credit when your network books through you." },
  { Icon: ChartLineUp, t: "Deliverables hub + reviews", d: "One place to share media and collect 5-star reviews that win the next listing." },
];

export default function PenthousePage() {
  const { user } = useAuth();
  const [active, setActive] = useState(!!user.penthouse_partner);
  const [busy, setBusy] = useState(false);
  const [shoots, setShoots] = useState(3);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    client.get("/penthouse/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("session_id");
    if (p.get("penthouse") === "success" && sid) {
      client.get(`/penthouse/status/${sid}`).then(({ data }) => {
        if (data.active) {
          setActive(true);
          toast.success("Welcome to Penthouse Partner — your first month is on us!");
          setTimeout(() => window.location.assign("/"), 1800);
        }
      }).catch(() => {});
    } else if (p.get("penthouse") === "cancel") {
      toast("Checkout canceled — you can start any time.");
    }
  }, []);

  const [planInterval, setPlanInterval] = useState("month");

  const start = async (interval = planInterval) => {
    setBusy(true);
    try {
      const { data } = await client.post("/penthouse/subscribe", { origin_url: window.location.origin, interval });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const monthly = Number(cfg?.monthly_price ?? 45);
  const annual = Number(cfg?.annual_price ?? 450);
  const perks = (cfg?.perks && cfg.perks.length) ? cfg.perks : INCLUDED.map((x) => x.t);
  const money = (n) => "$" + Number(n).toLocaleString();
  const yearlyValue = shoots * 12 * PER_SHOOT_VALUE;
  const savings = Math.max(0, yearlyValue - annual);

  return (
    <div className="max-w-4xl space-y-8" data-testid="penthouse-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-[#4A0000] text-white p-8 sm:p-10">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#B08D57]/20 blur-2xl" />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-soft-light" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-[#B08D57] text-xs font-bold uppercase tracking-widest"><Crown size={15} weight="fill" /> Penthouse Partner</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight">Every listing, elevated.</h1>
          <p className="text-white/70 mt-3 max-w-xl text-base">Get the 360° walkthrough and drone included on <b className="text-white">every</b> All-Inclusive shoot. Your first month is free, then just {money(monthly)}/mo — or {money(annual)}/yr and save.</p>
          {active ? (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#B08D57] text-[#1A1715] font-semibold rounded-[5px] px-5 py-2.5" data-testid="penthouse-active-badge"><CheckCircle size={18} weight="fill" /> You're a Penthouse Partner</div>
          ) : (
            <>
              {/* Plan choice — 30-day free trial either way, default monthly */}
              <div className="mt-6 inline-flex flex-col sm:flex-row gap-2" data-testid="penthouse-plan-choice">
                {[
                  { key: "month", title: "Monthly", price: `${money(monthly)}/mo`, note: "after 30-day trial" },
                  { key: "year", title: "Annual", price: `${money(annual)}/yr`, note: `2 months free · save ${money(Math.max(0, monthly * 12 - annual))}` },
                ].map((p) => {
                  const on = planInterval === p.key;
                  return (
                    <button key={p.key} data-testid={`penthouse-plan-${p.key}`} onClick={() => setPlanInterval(p.key)}
                      className={`relative text-left rounded-[6px] border px-4 py-2.5 transition ${on ? "border-[#B08D57] bg-[#B08D57]/20 ring-1 ring-[#B08D57]/60" : "border-white/20 bg-white/5 hover:bg-white/10"}`}>
                      {p.key === "year" && <span className="absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-wide bg-[#B08D57] text-[#1A1715] rounded-full px-1.5 py-0.5">Best value</span>}
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-white">{on && <CheckCircle size={14} weight="fill" className="text-[#E4C97E]" />}{p.title} · <span className="font-mono text-[#E4C97E]">{p.price}</span></span>
                      <span className="block text-[11px] text-white/50 mt-0.5">{p.note}</span>
                    </button>
                  );
                })}
              </div>
              <div>
                <Button data-testid="penthouse-start-btn" onClick={() => start()} disabled={busy}
                  className="relative overflow-hidden mt-4 bg-[#B08D57] hover:bg-[#C59B47] text-[#1A1715] font-semibold text-base px-7 py-6 rounded-[5px] shadow-lg shadow-[#B08D57]/30 group">
                  <motion.span aria-hidden animate={{ x: ["-130%", "230%"] }} transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.8, ease: "easeInOut" }}
                    className="absolute inset-y-0 w-1/3 bg-white/40 blur-md -skew-x-12 pointer-events-none" />
                  <Crown size={18} weight="fill" className="mr-2 transition-transform group-hover:scale-110 relative" />
                  <span className="relative">{busy ? "Starting…" : `Start free — then ${planInterval === "year" ? `${money(annual)}/yr` : `${money(monthly)}/mo`}`}</span>
                </Button>
              </div>
            </>
          )}
          <p className="text-white/40 text-xs mt-3">No charge today · cancel anytime · then {money(monthly)}/mo or {money(annual)}/yr</p>
        </div>
      </div>

      {/* Savings calculator */}
      <div className="rounded-xl border border-[#E9DDC6] bg-white p-6" data-testid="penthouse-savings">
        <h2 className="font-display text-2xl text-[#1A1A1A]">Do the math</h2>
        <p className="text-sm text-[#5C5A58] mt-1">The 360 tour + drone are worth about <b>${PER_SHOOT_VALUE}</b> per shoot. Here's your year with Penthouse.</p>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm text-[#5C5A58]">Shoots per month</span>
          <input data-testid="penthouse-shoots-input" type="range" min="1" max="12" value={shoots} onChange={(e) => setShoots(Number(e.target.value))} className="flex-1 accent-[#4A0000]" />
          <span className="font-display text-xl text-[#4A0000] w-8 text-center">{shoots}</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="rounded-lg bg-[#FAF9F6] p-4 text-center"><p className="text-xs text-[#5C5A58]">Value / year</p><p className="font-display text-2xl text-[#1A1A1A]" data-testid="penthouse-yearly-value">${yearlyValue.toLocaleString()}</p></div>
          <div className="rounded-lg bg-[#FAF9F6] p-4 text-center"><p className="text-xs text-[#5C5A58]">You pay / year</p><p className="font-display text-2xl text-[#1A1A1A]">{money(annual)}</p></div>
          <div className="rounded-lg bg-[#4A0000] p-4 text-center"><p className="text-xs text-white/60">You save</p><p className="font-display text-2xl text-[#B08D57]" data-testid="penthouse-savings-amount">${savings.toLocaleString()}</p></div>
        </div>
      </div>

      {/* What's included */}
      <div>
        <h2 className="font-display text-2xl text-[#1A1A1A] mb-4">What's included</h2>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3" data-testid="penthouse-included-bento">
          {perks.map((perk, i) => {
            const match = INCLUDED.find((x) => x.t.toLowerCase() === String(perk).toLowerCase());
            const Icon = match?.Icon || CheckCircle;
            const spans = ["sm:col-span-4", "sm:col-span-2", "sm:col-span-2", "sm:col-span-4", "sm:col-span-3", "sm:col-span-3"];
            return (
              <div key={`${perk}-${i}`} className={`flex items-start gap-3 rounded-[5px] border border-[#E9DDC6] bg-white p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-premium ${spans[i % spans.length]}`}>
                <span className="grid place-items-center w-10 h-10 rounded-full bg-[#4A0000]/5 text-[#4A0000] shrink-0"><Icon size={20} weight="duotone" /></span>
                <div><p className="font-semibold text-[#1A1A1A]">{match?.t || perk}</p>{match?.d && <p className="text-sm text-[#5C5A58] mt-0.5">{match.d}</p>}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Example branded listing presentation */}
      <div data-testid="penthouse-example-deck">
        <h2 className="font-display text-2xl text-[#1A1A1A] mb-1">See a Penthouse listing presentation</h2>
        <p className="text-sm text-[#5C5A58] mb-4">Type an address, verify the AI research, and hand your seller a client-ready deck like this — branded entirely to you.</p>
        <div className="rounded-[6px] border border-[#E9DDC6] overflow-hidden shadow-premium-lg grid md:grid-cols-2">
          <div className="relative bg-[#4A0000] text-white p-7 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#B08D57]/20 blur-2xl" />
            <p className="text-[10px] font-black tracking-[0.3em] uppercase text-[#B08D57] relative">Listing Presentation</p>
            <h3 className="font-display text-2xl mt-3 leading-tight relative">Renovated Ranch on a Quiet Cul-de-Sac</h3>
            <p className="text-white/55 text-sm mt-1 relative">825 Gretna Green Dr · Charlotte, NC</p>
            <div className="flex gap-4 mt-4 text-sm text-white/85 relative"><span>3 Bed</span><span>2 Bath</span><span>1,465 Sqft</span></div>
            <div className="flex items-center gap-2.5 mt-7 relative">
              <div className="w-10 h-10 rounded-full bg-[#B08D57]/25 grid place-items-center text-[#B08D57] font-display text-lg ring-2 ring-[#B08D57]/40">A</div>
              <div><p className="text-sm font-semibold">Your name & photo</p><p className="text-[11px] text-white/50">Your brokerage · your colors</p></div>
            </div>
          </div>
          <div className="bg-[#FBFAF7] p-7">
            <p className="text-[10px] font-black tracking-[0.25em] uppercase text-[#B08D57]">Executive Summary</p>
            <p className="font-display text-4xl text-[#4A0000] mt-2">$315,000</p>
            <p className="text-sm text-[#5C5A58]">~$215/sqft · 10–18 days to offer · 98–100% list-to-sale</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {[["Median sold", "$298K"], ["Median DOM", "12 days"], ["Active listings", "9"], ["3-yr appreciation", "+31%"]].map(([l, v]) => (
                <div key={l} className="rounded-[5px] border border-[#E9DDC6] bg-white p-3">
                  <p className="text-[9px] uppercase tracking-wider text-[#9A968E] font-semibold">{l}</p>
                  <p className="font-display text-xl text-[#4A0000] mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#9A968E] mt-3">Illustrative example. Every figure is AI-drafted, then verified by you before it's shared.</p>
          </div>
        </div>
      </div>

      {/* Options during booking */}
      <div>
        <h2 className="font-display text-2xl text-[#1A1A1A] mb-4">Your options at booking</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {OPTIONS.map(({ t, d }) => (
            <div key={t} className="rounded-xl border border-[#E9DDC6] bg-white p-4">
              <p className="font-semibold text-[#4A0000] flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#B08D57]" />{t}</p>
              <p className="text-sm text-[#5C5A58] mt-1">{d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tools to win more listings */}
      <div>
        <h2 className="font-display text-2xl text-[#1A1A1A] mb-4">Tools that win you more listings</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {TOOLS.map(({ Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-[#E9DDC6] bg-white p-5">
              <Icon size={24} weight="duotone" className="text-[#B08D57]" />
              <p className="font-semibold text-[#1A1A1A] mt-2">{t}</p>
              <p className="text-sm text-[#5C5A58] mt-1">{d}</p>
            </div>
          ))}
        </div>
      </div>

      {!active && (
        <div className="text-center py-2">
          <Button data-testid="penthouse-start-btn-2" onClick={start} disabled={busy} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white text-base px-7 py-6 rounded-[5px]">
            <Crown size={18} weight="fill" className="mr-2" />{busy ? "Starting…" : "Become a Penthouse Partner — free for a month"}
          </Button>
        </div>
      )}
    </div>
  );
}
